import { useState } from 'react';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ImportFilesDialog } from '@/components/finance/bank-ingestion';
import { useAuth } from '@/contexts/AuthContext';
import { useCampaignStore } from '@/stores';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useOnboardingStatus, isOnboardingIncomplete, readyOnboardingStatus } from '@/hooks/queries/useFinanceOnboarding';
import { useCloseNavBadge } from '@/hooks/useCloseNavBadge';
import { ShortcutReference } from './shared';
import { readyProfile } from '@/lib/complianceProfileRead';

/**
 * Finance primary nav: verb/noun surfaces — Dashboard, Close the period (N),
 * Donations, Contacts, Loans, Debts, Reports, Settings. Contacts, Loans, and Debts
 * are working data (people, Schedule-C loans, Schedule-D debts that feed report
 * schedules), so they live as first-class tabs rather than under Settings; Settings
 * stays the last-position tab rather than a masthead gear. Donations is the entry
 * point to Stripe Connect onboarding + the live donation dashboard (`/admin/finance/
 * payments`) — it sits high in the nav (right after Close) because it was the only
 * money-in surface with NO in-app link, leaving the onboarding page reachable only by
 * typing the URL. It is NOT jurisdiction-gated: every committee can accept online
 * gifts. The Settings tab carries a small amber "!" while setup is incomplete — the
 * only onboarding signal now the gear is gone. Debts is federal-only (Schedule D is a
 * Form 3 concept, so `DebtsCard` renders null off-federal); its tab is suppressed for
 * a non-federal committee so no one lands on a blank page. The `ObligationBar` chip
 * strip and the retired bell + `NotificationSheet` are not mounted here; `ObligationBar`
 * lives in `CloseLayout`'s masthead slot.
 */
type FinanceTab = {
  key: 'dashboard' | 'close' | 'donations' | 'contacts' | 'loans' | 'debts' | 'reports' | 'settings';
  label: string;
  to: string;
  exact: boolean;
  // Debts (FEC Schedule D) only applies to a federal committee; gated below.
  federalOnly?: boolean;
};

const PRIMARY_TABS: FinanceTab[] = [
  { key: 'dashboard', label: 'Dashboard',        to: '/admin/finance',          exact: true },
  { key: 'close',     label: 'Close the period', to: '/admin/finance/close',    exact: false },
  { key: 'donations', label: 'Donations',        to: '/admin/finance/payments', exact: false },
  { key: 'contacts',  label: 'Contacts',         to: '/admin/finance/contacts', exact: false },
  { key: 'loans',     label: 'Loans',            to: '/admin/finance/loans',    exact: false },
  { key: 'debts',     label: 'Debts',            to: '/admin/finance/debts',    exact: false, federalOnly: true },
  { key: 'reports',   label: 'Reports',          to: '/admin/finance/reports',  exact: false },
  { key: 'settings',  label: 'Settings',         to: '/admin/finance/settings', exact: false },
];

export function FinanceLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { user } = useAuth();
  const isPlatformAdmin = user?.isPlatformAdmin ?? false;
  const canViewFinance = useCampaignStore((s) => s.canView('Finance'));

  const onboardingRead = useOnboardingStatus();
  const onboardingStatus = readyOnboardingStatus(onboardingRead);
  const onboardingUnready = onboardingRead.status === 'failed' || onboardingRead.status === 'offline';
  const complianceProfile = readyProfile(useComplianceProfile());
  const closeBadgeCount = useCloseNavBadge();

  // Debts (Schedule D) is a federal Form 3 concept — mirror DebtsCard's own
  // federal-only render so a non-federal committee never gets a blank Debts tab.
  const isFederal = complianceProfile?.level === 'federal';
  const visibleTabs = PRIMARY_TABS.filter((tab) => !tab.federalOnly || isFederal);

  if (!isPlatformAdmin && !canViewFinance) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don&apos;t have access to the Finance module.</p>
      </div>
    );
  }

  // Setup is "complete enough to enter the finance area" once a treasurer is
  // designated. This reads `treasurerDesignated` off the View-gated onboarding
  // STATUS endpoint, NOT the Manage-only `GET /api/finance/settings` — that
  // Manage gate 403'd for a Finance:View user and dropped them onto the setup
  // wall, locking view-only users out of the whole finance area.
  const setupComplete = onboardingStatus?.treasurerDesignated === true;

  if (onboardingUnready) {
    return (
      <div className="w-full space-y-4">
        <h1 className="text-2xl font-semibold text-foreground">Finance</h1>
        <p className="text-sm text-red-700" role="alert">
          {onboardingRead.status === 'offline'
            ? "You're offline, so your finance setup can't load."
            : "Couldn't load your finance setup. Refresh to try again."}
        </p>
      </div>
    );
  }

  if (onboardingStatus && !setupComplete) {
    return (
      <div className="w-full space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold text-foreground">Finance</h1>
        </div>
        <div className="flex items-center justify-center h-48">
          {/* Deep-link straight to the treasurer form's page (the co-located
              Campaign & Committee section, post Settings-IA reorg) rather than
              the Settings hub root — the FinanceSetupCard that designates the
              treasurer lives there, so this saves the user the extra hub click.
              Authz note: this gate shows for Finance:View users, but the
              destination's FinanceSetupCard self-hides unless the user can
              MANAGE Finance. A view-only user therefore can't complete setup
              wherever we send them; that pre-existing asymmetry is out of scope
              here and left unchanged. */}
          <p className="text-muted-foreground text-center">
            Complete finance setup in{' '}
            <Link to="/app/settings/campaign-committee" className="text-primary underline">
              Campaign &amp; Committee settings
            </Link>
            {' '}to get started.
          </p>
        </div>
      </div>
    );
  }

  // Setup is "incomplete" when EITHER a bank OR a processor is missing — the
  // single source of truth lives in `isOnboardingIncomplete` (which also treats
  // an undefined/loading status as NOT incomplete, so a cold load never flashes a
  // false "!"). Sharing it with the dashboard's setup card keeps the two surfaces
  // from drifting apart.
  const showSetupIndicator = isOnboardingIncomplete(onboardingStatus);

  function renderTab(tab: FinanceTab) {
    const isActive = tab.exact
      ? pathname === tab.to || pathname === `${tab.to}/`
      : pathname.startsWith(tab.to);

    // (N) badge per ticket § `(N)` badge semantics: omit parenthetical entirely
    // when count is 0; otherwise render inline in the label.
    const label = tab.key === 'close' && closeBadgeCount > 0
      ? `${tab.label} (${closeBadgeCount})`
      : tab.label;

    return (
      <Link
        key={tab.to}
        to={tab.to}
        className={cn(
          'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5',
          isActive
            ? 'border-primary text-primary'
            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30',
        )}
      >
        {label}
        {tab.key === 'settings' && showSetupIndicator && (
          <sup
            data-testid="settings-setup-indicator"
            className="ml-0.5 inline-flex items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 leading-none"
          >
            <span aria-hidden>!</span>
            <span className="sr-only">setup incomplete</span>
          </sup>
        )}
      </Link>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-foreground">Finance</h1>
        <div className="flex items-center gap-2">
          <ShortcutReference open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
          {/* Import is the treasurer's most frequent action and no longer hides
              under the Settings gear: it rides the masthead on every finance
              tab. Quiet outline, so it stays available without competing with
              each page's own primary action. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="finance-import-button"
            onClick={() => setImportOpen(true)}
            className="gap-1.5"
          >
            <Upload className="h-4 w-4" aria-hidden />
            Upload files
          </Button>
        </div>
      </div>

      {/* Top-level nav. ObligationBar + NotificationSheet live outside this
          shell — ObligationBar lives in `CloseLayout`'s masthead slot. */}
      <div className="flex items-center gap-4 border-b border-border">
        <nav data-testid="primary-nav" className="flex gap-1">
          {visibleTabs.map(renderTab)}
        </nav>
      </div>

      <Outlet />

      <ImportFilesDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
